import { LogoHeader } from "@/components/logoHeader";
import TooltipInfo from "@/components/tooltipInfo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { pedagogico } from "@/constants/pedagogico";
import { secretaria_financeiro } from "@/constants/secretaria_financeiro";
import { getSolicitacao } from "@/http/getSolicitacao";
import obterRespostaByArray from "@/utils/obterRespostaByArray";
import { useQuery } from "@tanstack/react-query";
import {
  Ban,
  CircleCheckBig,
  Info,
  LoaderCircle,
  PaperclipIcon,
} from "lucide-react";
import moment from "moment";
import { Link, useParams } from "react-router-dom";

import "moment/locale/pt-br";
import { downloadFileFromBase64 } from "@/utils/downloadFileFromBase64";
import { getTempoResposta } from "@/utils/getTempoResposta";
import { BadgeStatus } from "@/components/badgeStatus";
import converterBytes from "@/utils/converterBits";
import { encurtarNome } from "@/utils/encurtarNome";
import { useState } from "react";
import { getAssetsPresignedUrlAluno } from "@/http/getAssetsPresignedUrlAluno";
import { Card } from "@/components/ui/card";

moment.updateLocale("pt-br", {
  relativeTime: {
    future: "em %s",
    past: "há %s",
    s: "alguns segundos",
    ss: "%d segundos",
    m: "um minuto",
    mm: "%d minutos",
    h: "uma hora",
    hh: "%d horas",
    d: "um dia",
    dd: "%d dias",
    M: "um mês",
    MM: "%d meses",
    y: "um ano",
    yy: "%d anos",
  },
});

const setores = {
  pedagogico: "Pedagógico",
  financeiro: "Financeiro",
};

function SolicitacaoID() {
  let { id } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["solicitacao", id],
    queryFn: () => getSolicitacao({ id }),
    retry: 1,
  });

  const setor = setores[data?.setor] || "Secretaria";
  const servicos =
    data?.setor === "pedagogico" ? pedagogico : secretaria_financeiro;
  const servico = servicos?.find((element) => element.id === data?.servico);

  const atendenteComUnidade = `${encurtarNome(data?.atendente?.nome ?? "Atendente")} - ${
    data?.unidade?.nome ?? "Unidade"
  }`;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 h-screen justify-center items-center">
        <LoaderCircle className="animate-spin text-slate-400 size-10" />
        <p className="text-zinc-300 text-center">Buscando..</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-2 h-screen justify-center items-center">
        <Ban className="text-red-500 size-10" />
        <p className="text-zinc-400 text-center mb-1">
          Solicitação não encontrada.
        </p>
        <Link to="/">
          <Button>VOLTAR AO INÍCIO</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[500px] pb-8">
      <LogoHeader />
      <div className="min-h-[90px] bg-white flex flex-col justify-center px-4">
        <div className="flex flex-row justify-between items-center">
          <p className="text-2xl font-bold text-zinc-600">Solicitação</p>
          {data && (
            <BadgeStatus
              finalizado={data.finalizado}
              inProgress={data.inProgress}
            />
          )}
        </div>
        <div className="flex flex-row gap-2 items-center justify-between">
          <p className="text-[11px] text-zinc-400">{id}</p>
          {data?.finalizado ? (
            <p className="text-[11px] text-zinc-400">
              Tempo de resposta{" "}
              {getTempoResposta(data.createdAt, data.finalizado_data)}
            </p>
          ) : (
            <p className="text-[11px] text-zinc-400">
              {" "}
              {data
                ? `Enviado ${moment(data.createdAt).fromNow()}`
                : "Enviado recentemente"}
            </p>
          )}
        </div>
      </div>

      <div className="mt-2 p-4 flex flex-col">
        <div className="mb-4">
          <p className="font-semibold">Detalhes</p>
        </div>
        <div className="flex flex-col bg-white rounded-md border border-border">
          <CardInfo
            divider
            label="Aluno"
            value={
              data
                ? obterRespostaByArray(data.solicitacao, "nome_completo")
                : ""
            }
          />
          <CardInfo
            divider
            label="Solicitação"
            value={servico ? servico.label : ""}
          />
          <CardInfo divider label="Setor" value={setor} />
          {servico && servico.prazo && (
            <CardInfo
              label="Tempo estimado de resposta"
              value={servico.prazo}
              helpText="Considerando dias úteis e sábados até 18h."
            />
          )}
        </div>
      </div>

      <div className="p-4 flex flex-col">
        <p className="font-semibold">Respostas</p>
        {data && data.respostas.length === 0 && (
          <div className="mt-3">
            <AlertSemResposta />
          </div>
        )}
      </div>

      <div className="px-4 flex flex-col gap-2">
        {data &&
          data.respostas.length > 0 &&
          data.respostas.map((item, idx) => (
            <div key={idx}>
              {item.type === "arquivo" && (
                <CardInfoRespostaArquivo
                  size={item.size}
                  originalname={item.originalname.split(".")[0]}
                  label={atendenteComUnidade}
                  base64={item.resposta}
                  createdAt={moment(item.createdAt).format("DD/MM/YY HH:mm")}
                />
              )}
              {item.type === "arquivo_link" && (
                <CardInfoRespostaArquivoLink
                  id={id}
                  size={item.size}
                  originalname={item.originalname.split(".")[0]}
                  label={atendenteComUnidade}
                  link={item.resposta}
                  createdAt={moment(item.createdAt).format("DD/MM/YY HH:mm")}
                />
              )}
              {item.type !== "arquivo_link" && item.type !== "arquivo" && (
                <CardInfoRespostaText
                  label={atendenteComUnidade}
                  value={item.resposta}
                  createdAt={moment(item.createdAt).format("DD/MM/YY HH:mm")}
                />
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

export default SolicitacaoID;

function CardInfo({ label, value, divider, helpText }) {
  return (
    <div
      className={`px-4 py-3 flex flex-row justify-between items-center ${
        divider ? "border-b" : "border-0"
      } border-border`}
    >
      <div className="flex flex-row items-center">
        <p className="font-bold">{label}</p>
        {helpText && <TooltipInfo helpText={helpText} />}
      </div>
      <p className="text-zinc-400 truncate max-w-[240px] overflow-hidden whitespace-nowrap">
        {value}
      </p>
    </div>
  );
}

function CardInfoRespostaText({ label, value, createdAt }) {
  return (
    <div className="px-4 py-3 flex flex-col gap-2 bg-white border border-border rounded-md">
      <div className="flex flex-row items-center justify-between">
        <Badge variant="outline">{label}</Badge>
        <p className="text-[11px] text-zinc-400">Enviado: {createdAt}</p>
      </div>
      <div className="p-2 w-full max-w-full overflow-x-auto">
        <div
          className="prose max-w-full text-zinc-600 break-words [&_pre]:overflow-auto [&_pre]:max-w-full [&_a]:text-blue-600 [&_a]:underline"
          dangerouslySetInnerHTML={{
            __html: value,
          }}
        />
      </div>
    </div>
  );
}

function CardInfoRespostaArquivo({
  label,
  base64,
  originalname,
  createdAt,
  size,
}) {
  return (
    <div className="px-4 py-3 flex flex-col gap-2 bg-white border border-border rounded-md">
      <div className="flex flex-row items-center justify-between">
        <Badge variant="outline">{label}</Badge>
        <p className="text-[11px] text-zinc-400">Enviado: {createdAt}</p>
      </div>
      <div className="px-1 flex flex-col gap-1 mb-2 mt-4">
        <p className="text-zinc-400 text-[12px]">Arquivo recebido:</p>
        <div className="flex items-center justify-between py-4 pr-5 pl-4 text-sm/6 border border-border rounded-md">
          <div className="flex w-0 flex-1 items-center">
            <PaperclipIcon
              aria-hidden="true"
              className="size-5 shrink-0 text-gray-400"
            />
            <div className="ml-4 flex min-w-0 flex-1 gap-2">
              <span className="truncate min-w-0 flex-1 font-medium">
                {originalname}
              </span>
              <span className="shrink-0 text-gray-400">{size}</span>
            </div>
          </div>
          <div className="ml-4 shrink-0">
            <div
              onClick={() => downloadFileFromBase64(base64, originalname)}
              className="font-medium text-red-600 hover:text-red-500 cursor-pointer"
            >
              Download
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CardInfoRespostaArquivoLink({
  label,
  link,
  originalname,
  createdAt,
  size,
  id,
}) {
  const [loading, setLoading] = useState(false);

  const downloadFileFromLink = async () => {
    try {
      setLoading(true);
      const response = await getAssetsPresignedUrlAluno({ link, id });
      const url = response.url;

      const res = await fetch(url);
      const blob = await res.blob();

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = originalname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Erro ao forçar download:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-3 flex flex-col gap-2 bg-white border border-border rounded-md">
      <div className="flex flex-row items-center justify-between">
        <Badge variant="outline">{label}</Badge>
        <p className="text-[11px] text-zinc-400">Enviado: {createdAt}</p>
      </div>
      <div className="px-1 flex flex-col gap-1 mb-2 mt-4">
        <p className="text-zinc-400 text-[12px]">Arquivo recebido:</p>
        <div className="flex items-center justify-between py-4 pr-5 pl-4 text-sm/6 border border-border rounded-md">
          <div className="flex w-0 flex-1 items-center">
            <PaperclipIcon
              aria-hidden="true"
              className="size-5 shrink-0 text-gray-400"
            />
            <div className="ml-4 flex min-w-0 flex-1 gap-2">
              <span className="truncate min-w-0 flex-1 font-medium">
                {originalname}
              </span>
              {size && (
                <span className="shrink-0 text-gray-400">
                  {converterBytes(size)}
                </span>
              )}
            </div>
          </div>
          <div className="ml-4 shrink-0">
            {loading ? (
              <div className="motion-scale-in-[0.97] motion-translate-x-in-[0%] motion-translate-y-in-[9%] motion-opacity-in-[0%] motion-duration-[0.17s]/scale motion-duration-[0.16s]/opacity flex items-center gap-1 font-medium text-red-400 cursor-not-allowed">
                {loading && (
                  <LoaderCircle className="animate-spin text-red-400 size-4" />
                )}
                Baixando...
              </div>
            ) : (
              <div
                onClick={downloadFileFromLink}
                className="font-medium text-red-600 hover:text-red-500 cursor-pointer"
              >
                Download
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertSemResposta() {
  return (
    <div className="flex items-center justify-center bg-white/60 px-4 py-2 rounded-md border border-border">
      <p className="text-[11px] text-zinc-400 leading-relaxed">
        No momento, Não há uma resposta disponível para esta solicitação. Por
        favor, aguarde, pois em breve uma resposta será fornecida.
      </p>
    </div>
  );
}
